// src/services/assets/workflowResolver.js

/**
 * =====================================================
 * WORKFLOW RESOLVER
 * =====================================================
 *
 * PURPOSE:
 * - Single source of truth for workflow routing
 * - Determines correct workflow for any holder pair
 * - No raw strings — all enums
 * - No UI logic — pure service layer
 *
 * RULES:
 * - Never call this from UI directly
 * - Always call from assetTransactionService
 * - Never add routing logic outside this file
 * - All approved flows must be handled here
 *
 * APPROVED FLOWS:
 * ─────────────────────────────────────────────────────
 * STORE      → STAFF/TECHNICIAN/EMPLOYEE/SITE/STORE   DIRECT FINAL
 * STAFF      → STORE                                  RETURN APPROVAL
 * SITE       → STORE                                  RETURN APPROVAL
 * TECHNICIAN → STORE                                  RETURN APPROVAL  ← ADDED
 * STAFF      → STAFF                                  ACCEPTANCE REQUIRED
 * SITE       → STAFF                                  ACCEPTANCE REQUIRED
 * STAFF      → SITE                                   ACCEPTANCE REQUIRED
 * STAFF      → TECHNICIAN                             ACCEPTANCE REQUIRED  ← ADDED
 * TECHNICIAN → STAFF                                  ACCEPTANCE REQUIRED  ← ADDED
 * TECHNICIAN → TECHNICIAN                             ACCEPTANCE REQUIRED  ← ADDED
 * TECHNICIAN → SITE                                   ACCEPTANCE REQUIRED  ← ADDED
 * SITE       → TECHNICIAN                             ACCEPTANCE REQUIRED  ← ADDED
 * ─────────────────────────────────────────────────────
 *
 * USED BY:
 * - assetTransactionService.js (createTransferRequest)
 * =====================================================
 */

import { HOLDER_TYPES } from "../../constants/holderTypes";

import { WORKFLOW_TYPES }      from "./workflowTypes";
import { WORKFLOW_STATES }     from "./workflowStates";
import { TRANSACTION_CLASSES } from "./transactionClasses";
import { WORKFLOW_BUCKETS }    from "./workflowBuckets";

// =====================================================
// RESOLVER
// =====================================================

/**
 * resolveWorkflow
 *
 * @param {Object} params
 * @param {string} params.senderHolderType   - HOLDER_TYPES.*
 * @param {string} params.receiverHolderType - HOLDER_TYPES.*
 *
 * @returns {{
 *   workflowType: string,
 *   workflowState: string,
 *   transactionClass: string,
 *   visibilityBucket: string | null,
 *   requiresAcceptance: boolean,
 *   requiresReturnApproval: boolean,
 *   shouldMutateHolderImmediately: boolean,
 *   isFinalized: boolean,
 * }}
 */

export const resolveWorkflow = ({
    senderHolderType,
    receiverHolderType,
}) => {

    // ─────────────────────────────────────────
    // INPUT VALIDATION
    // ─────────────────────────────────────────

    if (!senderHolderType || !receiverHolderType) {
        throw new Error(
            "senderHolderType and receiverHolderType are required"
        );
    }

    // =====================================================
    // ROUTE 1 — DIRECT FINAL TRANSFER
    //
    // STORE → STAFF / TECHNICIAN / EMPLOYEE / SITE / STORE
    //
    // - Immediately finalized
    // - Holder mutated instantly
    // - No pending queue
    // - No approval required
    // - No acceptance required
    // =====================================================

    if (
        senderHolderType === HOLDER_TYPES.STORE &&
        [
            HOLDER_TYPES.STAFF,
            HOLDER_TYPES.TECHNICIAN,
            HOLDER_TYPES.EMPLOYEE,
            HOLDER_TYPES.SITE,
            HOLDER_TYPES.STORE,
        ].includes(receiverHolderType)
    ) {
        return {
            workflowType:                  WORKFLOW_TYPES.DIRECT_FINAL,
            workflowState:                 WORKFLOW_STATES.FINALIZED,
            transactionClass:              TRANSACTION_CLASSES.TRANSFER,
            visibilityBucket:              null,
            requiresAcceptance:            false,
            requiresReturnApproval:        false,
            shouldMutateHolderImmediately: true,
            isFinalized:                   true,
        };
    }

    // =====================================================
    // ROUTE 2 — RETURN APPROVAL REQUIRED
    //
    // STAFF      → STORE
    // SITE       → STORE
    // TECHNICIAN → STORE  ← ADDED: technician returning tool to store
    //
    // - Creates RETURN_PENDING transaction
    // - Visible only in ReceiveReturns
    // - Store must physically receive asset
    // - Holder restored to MAIN STORE on receive
    // - Isolated from transfer engine
    // =====================================================

    if (
        [
            HOLDER_TYPES.STAFF,
            HOLDER_TYPES.SITE,
            HOLDER_TYPES.TECHNICIAN,   // ← ADDED
        ].includes(senderHolderType) &&
        receiverHolderType === HOLDER_TYPES.STORE
    ) {
        return {
            workflowType:                  WORKFLOW_TYPES.RETURN_APPROVAL,
            workflowState:                 WORKFLOW_STATES.RETURN_PENDING,
            transactionClass:              TRANSACTION_CLASSES.RETURN,
            visibilityBucket:              WORKFLOW_BUCKETS.RECEIVE_RETURNS,
            requiresAcceptance:            false,
            requiresReturnApproval:        true,
            shouldMutateHolderImmediately: false,
            isFinalized:                   false,
        };
    }

    // =====================================================
    // ROUTE 3 — ACCEPTANCE REQUIRED
    //
    // STAFF      → STAFF
    // SITE       → STAFF
    // STAFF      → SITE
    // STAFF      → TECHNICIAN  ← ADDED: staff passing tool to technician
    // TECHNICIAN → STAFF       ← ADDED: technician passing tool to staff
    // TECHNICIAN → TECHNICIAN  ← ADDED: technician to technician transfer
    // TECHNICIAN → SITE        ← ADDED: technician deploying to site
    // SITE       → TECHNICIAN  ← ADDED: site to technician
    //
    // - Creates PENDING_ACCEPTANCE transaction
    // - Visible only in PendingTransfers
    // - Receiver must accept before custody changes
    // - Holder mutated only after acceptance
    // - Isolated from return engine
    // =====================================================

    // Peer holder types — all non-store field holders
    const FIELD_HOLDERS = [
        HOLDER_TYPES.STAFF,
        HOLDER_TYPES.TECHNICIAN,
        HOLDER_TYPES.SITE,
    ];

    if (
        FIELD_HOLDERS.includes(senderHolderType) &&
        FIELD_HOLDERS.includes(receiverHolderType)
    ) {
        return {
            workflowType:                  WORKFLOW_TYPES.ACCEPTANCE_REQUIRED,
            workflowState:                 WORKFLOW_STATES.PENDING_ACCEPTANCE,
            transactionClass:              TRANSACTION_CLASSES.TRANSFER,
            visibilityBucket:              WORKFLOW_BUCKETS.PENDING_TRANSFERS,
            requiresAcceptance:            true,
            requiresReturnApproval:        false,
            shouldMutateHolderImmediately: false,
            isFinalized:                   false,
        };
    }

    // =====================================================
    // INVALID FLOW — no matching route
    // =====================================================

    throw new Error(
        `Unsupported asset workflow: ${senderHolderType} → ${receiverHolderType}. ` +
        `Check approved flow matrix in workflowResolver.js`
    );
};